import { SERVER_URL } from "Constants";
import { enableDisableProviderProductsForEntities } from "Hooks/Api/apiProviderProducts";
import alertToast from "Util/ToastifyUtils";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import EntityList, { IEntityListHeaderProps } from "../EntityList/EntityList";
import If from "../If/If";
import TablePagination from "../Pagination/TablePagination";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";

export type CreditReportProductDetail = {
	id: string;
	name: string;
	bureau: string;
	type: string;
	previewLink: string;
	originalPrice: number;
	price: number;
	discount: number;
	recommended: boolean;
	available: boolean;
}

type Props = {
	list: CreditReportProductDetail[];
}

export const CreditReportProductList: React.FunctionComponent<Props> = ({ list }) => {
	const location = useLocation();
	const isOrganisationLevel = !location.pathname.match('business-entities');

	const sortedList = list.sort((a, b) => {
		if (a.recommended && !b.recommended) {
			return -1; 
		}
		if (!a.recommended && b.recommended) {
			return 1;
		}
		if (a.bureau !== b.bureau) {
			return a.bureau.localeCompare(b.bureau);
		}
		return a.name.localeCompare(b.name);
	});

	const [listState, setListState] = React.useState(sortedList.slice(0, 10));
	const [page, setPage] = React.useState(0);

	useEffect(() => {
		setListState(sortedList.slice(page * 10, (page + 1) * 10));
	}, [sortedList, page]);

	const handleToggle = async (item: CreditReportProductDetail) => {
		const response = await enableDisableProviderProductsForEntities(item.id, !item.available, isOrganisationLevel);
		if (response.status === 200) {
			alertToast(`Successfully ${item.available ? 'disabled' : 'enabled'} product`, 'success');
			setListState(listState.map(x => x.id === item.id ? { ...x, available: !x.available } : x));
		} else {
			alertToast('Failed to enable product', 'error');
		}
	}

	const getColumns = (): IEntityListHeaderProps<CreditReportProductDetail>[] => {
		return [
			{
				columnName: "id",
				displayName: "ID",
				value: item => item.id,
				hide: true
			},
			{
				columnName: "bureau",
				displayName: "Bureau",
				value: item => item.bureau,
			},
			{
				columnName: "name",
				displayName: "Credit Report Product",
				value: item => (
					<span 
						className={classNames('', { "icon-star-fill icon-right icon-pink": item.recommended })}
						data-for={`${item.id}-rc-tooltip`}
						data-tip={"Recommended product"}
					>
						{item.name}
						<If condition={item.recommended}>
							<ReactTooltip
								id={`${item.id}-rc-tooltip`}
								place="right"
								effect="solid"
								className="tooltip-box"
							/>
						</If>
					</span>
				),
			},
			{
				columnName: "type",
				displayName: "Type",
				value: item => (
					<>
						<span style={{display: "flex", flexDirection: "row", gap: "5px"}}>
							{item.type}
							<If condition={!!item.previewLink}>
								<span 
									className="icon-file-text icon-right icon-inline" 
									data-for={`${item.id}-pv-tooltip`}
									data-tip={"Preview"}
									onClick={() => 
										window.open(`${SERVER_URL}/api/files/document/${item.previewLink}`, '_blank')}
								/>
								<ReactTooltip
									id={`${item.id}-pv-tooltip`}
									place="right"
									effect="solid"
									className="tooltip-box"
								/>
							</If>
						</span>
					</>
				),
			},
			{
				columnName: "price",
				displayName: "Price",
				value: item => 
					<>
						<If condition={item.discount > 0}>
							<span style={{textDecoration: "line-through"}}>
								${item.originalPrice.toFixed(2)}
							</span>
							<br/>
							<span>${item.price.toFixed(2)}</span>
							<span>&nbsp;&nbsp;</span>
							<span style={{color: "white", background: "#DF1A6D", padding: "5px"}}>
								{item.discount}% off
							</span>
						</If>
						<If condition={item.discount === 0}>
							${item.price.toFixed(2)}
						</If>
					</>
			},
			{
				columnName: "available",
				displayName: "Available",
				value: item => 
					<ToggleSwitch 
						model={item} 
						modelProperty="available" 
						hideLabel
					/>,
				onClickValue: handleToggle,
			},
		];
	}

	return (
		<div className="collection-component">
			<EntityList
				collection={listState}
				columns={getColumns()}
				idColumn="id"
				sortColumn="name"
				sortDescending={false}
			/>
			{list.length > 10 && (
				<section className="collection__load">
					<TablePagination
						perPage={10}
						pageNo={page}
						totalRecords={list.length}
						onPageChange={setPage}
					/>
				</section>
			)}
		</div>
	);
}
